import { claimIdentity } from '../../../webmodule-common/other/api/user-security';
import { isEmptyOrSpace, sameText } from '../../../webmodule-common/other/ui/string-helper-functions';

import { userDataStore } from './current-user-data-store';

export interface LicensePoolData {
  totalLicenses: number;
  licensesAllocatedToDealer: number;
  licensesUsedByDealer: number;
}

export async function getLicenseData(deploymentId?: number): Promise<LicensePoolData> {
  const data: LicensePoolData = {
    totalLicenses: 0,
    licensesAllocatedToDealer: 0,
    licensesUsedByDealer: 0
  };

  await userDataStore.loadCoreDetails();

  const tentantList = userDataStore.allFranchiseeDetails.tenantList;
  const deployments =
    deploymentId === undefined
      ? (tentantList.deployments ?? [])
      : (tentantList.deployments?.filter(x => x.dealerDeploymentId === deploymentId) ?? []);

  deployments.forEach(deployment => {
    deployment.licensePools?.forEach(licensePool => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const licenseCount = licensePool.licensePool!.concurrentUsers;
      data.totalLicenses += licenseCount;
      data.licensesAllocatedToDealer += licenseCount - licensePool.availableAllocations;
      data.licensesUsedByDealer += licenseCount - licensePool.availableUsers;
    });
  });
  return data;
}

export async function getLicenseDataByBranch(
  deploymentId?: number,
  franchiseeId?: string,
  branchIds?: string[]
): Promise<LicensePoolData> {
  const data: LicensePoolData = {
    totalLicenses: 0,
    licensesAllocatedToDealer: 0,
    licensesUsedByDealer: 0
  };

  await userDataStore.loadCoreDetails();
  const cacheData = userDataStore.allFranchiseeDetails;

  const tentantList = cacheData.tenantList;
  const deployments =
    deploymentId === undefined
      ? (tentantList.deployments ?? [])
      : (tentantList.deployments?.filter(x => x.dealerDeploymentId === deploymentId) ?? []);

  deployments.forEach(deployment => {
    deployment.tenants?.forEach(tenantInfo => {
      const franchiseeDeploymentConfiguration = cacheData.franchisees.find(
        x =>
          x.franchiseeTenantId === tenantInfo.tenantId &&
          x.dealerDeploymentId === deployment.dealerDeploymentId &&
          (isEmptyOrSpace(franchiseeId) || x.id === franchiseeId)
      );

      const branches = cacheData.branches.filter(
        b =>
          b.dealerDeploymentId === franchiseeDeploymentConfiguration?.dealerDeploymentId &&
          b.franchiseeTenantId === franchiseeDeploymentConfiguration?.franchiseeTenantId &&
          (branchIds === undefined || branchIds.length === 0 || branchIds.includes(b.branchId))
      );

      const tenantUsers = deployment.userLoginInformation?.filter(x => x.tenantId === tenantInfo.tenantId) ?? [];

      function isInvalidBranch(id: string): boolean {
        return branches.find(c => c.branchId === id) === undefined;
      }

      let activeBranchUsersCount = 0;
      branches.forEach((branch, index) => {
        const isMainBranch = index === 0;

        function isSameBranch(claims: { [key: string]: string } | null) {
          const branchId = claims?.[claimIdentity.branchId] ?? '';
          return sameText(branchId, branch.branchId) || (isInvalidBranch(branchId) && isMainBranch);
        }

        const branchUsers = tenantUsers.filter(x => isSameBranch(x.claims));
        activeBranchUsersCount += branchUsers.filter(x => x.active).length ?? 0;
      });
      if (tenantInfo.licensePoolAllocated !== -1 && tenantInfo.licensePoolAllocations !== -1) {
        data.licensesUsedByDealer += activeBranchUsersCount;
        data.licensesAllocatedToDealer += tenantInfo.licensePoolAllocations;
      }
    });

    deployment.licensePools?.forEach(licensePool => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const licenseCount = licensePool.licensePool!.concurrentUsers;
      data.totalLicenses += licenseCount;
    });
  });
  return data;
}
